import { css } from 'lit';

export const styles = css`
  :host {
    display: inline-block;
  }

  /* Ensure ?hidden binding on slot elements collapses them correctly despite display:block on parts */
  [hidden] {
    display: none !important;
  }

  /* ─── Per-size container diameter ─── */
  :host([size='xxs']) { --asc-progress-circle-size: 64px; }
  :host([size='xs'])  { --asc-progress-circle-size: 160px; }
  :host([size='sm'])  { --asc-progress-circle-size: 200px; }
  :host([size='md'])  { --asc-progress-circle-size: 240px; }
  :host([size='lg'])  { --asc-progress-circle-size: 280px; }

  /* ─── Per-size value typography ─── */
  /* xxs: body font, text-sm/Semibold */
  :host([size='xxs']) {
    --asc-progress-circle-value-font-family:    var(--asc-font-family-body);
    --asc-progress-circle-value-font-size:      var(--asc-font-size-text-sm);
    --asc-progress-circle-value-line-height:    var(--asc-line-height-text-sm);
    --asc-progress-circle-value-letter-spacing: 0;
  }
  /* xs: display-xs/Semibold */
  :host([size='xs']) {
    --asc-progress-circle-value-font-family:    var(--asc-font-family-display);
    --asc-progress-circle-value-font-size:      var(--asc-font-size-display-xs);
    --asc-progress-circle-value-line-height:    var(--asc-line-height-display-xs);
    --asc-progress-circle-value-letter-spacing: 0;
  }
  /* sm: display-sm/Semibold */
  :host([size='sm']) {
    --asc-progress-circle-value-font-family:    var(--asc-font-family-display);
    --asc-progress-circle-value-font-size:      var(--asc-font-size-display-sm);
    --asc-progress-circle-value-line-height:    var(--asc-line-height-display-sm);
    --asc-progress-circle-value-letter-spacing: 0;
  }
  /* md: display-md/Semibold, tight tracking (-2px) */
  :host([size='md']) {
    --asc-progress-circle-value-font-family:    var(--asc-font-family-display);
    --asc-progress-circle-value-font-size:      var(--asc-font-size-display-md);
    --asc-progress-circle-value-line-height:    var(--asc-line-height-display-md);
    --asc-progress-circle-value-letter-spacing: -2px;
  }
  /* lg: display-lg/Semibold, tight tracking (-2px) */
  :host([size='lg']) {
    --asc-progress-circle-value-font-family:    var(--asc-font-family-display);
    --asc-progress-circle-value-font-size:      var(--asc-font-size-display-lg);
    --asc-progress-circle-value-line-height:    var(--asc-line-height-display-lg);
    --asc-progress-circle-value-letter-spacing: -2px;
  }

  /* ─── Per-size sublabel typography ─── */
  /* xxs/xs/sm: text-xs/Medium */
  :host([size='xxs']),
  :host([size='xs']),
  :host([size='sm']) {
    --asc-progress-circle-sublabel-font-size:   var(--asc-font-size-text-xs);
    --asc-progress-circle-sublabel-line-height: var(--asc-line-height-text-xs);
  }
  /* md/lg: text-sm/Medium */
  :host([size='md']),
  :host([size='lg']) {
    --asc-progress-circle-sublabel-font-size:   var(--asc-font-size-text-sm);
    --asc-progress-circle-sublabel-line-height: var(--asc-line-height-text-sm);
  }

  /* ─── Per-size inner-content padding ─── */
  /* Prevents long labels from overflowing the ring at smaller sizes */
  :host([size='xxs']) { --asc-progress-circle-content-padding: var(--asc-spacing-xs); }
  :host([size='xs'])  { --asc-progress-circle-content-padding: var(--asc-spacing-2xl); }
  :host([size='sm'])  { --asc-progress-circle-content-padding: var(--asc-spacing-3xl); }
  :host([size='md'])  { --asc-progress-circle-content-padding: var(--asc-spacing-4xl); }
  :host([size='lg'])  { --asc-progress-circle-content-padding: var(--asc-spacing-5xl); }

  /* ─── Base layout ─── */
  [part='base'] {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  /* Gap between ring and below-sublabel (xxs only) */
  :host([size='xxs']) [part='base'] {
    gap: var(--asc-progress-circle-gap, var(--asc-spacing-xs));
  }

  /* ─── Ring wrapper ─── */
  [part='ring-wrapper'] {
    position: relative;
    width: var(--asc-progress-circle-size);
    height: var(--asc-progress-circle-size);
    flex-shrink: 0;
  }

  /* ─── SVG ─── */
  [part='svg'] {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  /* ─── Track circle ─── */
  [part='track'] {
    stroke: var(--asc-progress-circle-track-color, var(--asc-bg-secondary));
  }

  /* ─── Fill arc ─── */
  [part='fill'] {
    stroke: var(--asc-progress-circle-fill-color, var(--asc-fg-brand-primary));
    transition: stroke-dashoffset var(--asc-progress-circle-transition-duration, 300ms) ease;
  }

  :host([status='empty']) [part='fill'] {
    stroke: var(
      --asc-progress-circle-fill-color-empty,
      var(--asc-progress-circle-fill-color, var(--asc-fg-brand-primary))
    );
  }

  :host([status='complete']) [part='fill'] {
    stroke: var(
      --asc-progress-circle-fill-color-complete,
      var(--asc-progress-circle-fill-color, var(--asc-fg-brand-primary))
    );
  }

  /* ─── Inner content overlay (xs–lg) ─── */
  [part='inner-content'] {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding-inline: var(--asc-progress-circle-content-padding);
    pointer-events: none;
  }

  /* ─── Sublabel inside ring (xs–lg) ─── */
  /* Slot element; only rendered when size is not xxs and sublabel content is assigned */
  [part='sublabel-inner'] {
    display:     block;
    font-family: var(--asc-progress-circle-sublabel-font-family, var(--asc-font-family-body));
    font-size:   var(--asc-progress-circle-sublabel-font-size);
    font-weight: var(--asc-progress-circle-sublabel-font-weight, var(--asc-font-weight-medium));
    line-height: var(--asc-progress-circle-sublabel-line-height);
    color:       var(--asc-progress-circle-sublabel-color, var(--asc-text-tertiary));
  }

  /* ─── Value label ─── */
  [part='value'] {
    font-family:    var(--asc-progress-circle-value-font-family);
    font-size:      var(--asc-progress-circle-value-font-size);
    font-weight:    var(--asc-progress-circle-value-font-weight, var(--asc-font-weight-semibold));
    line-height:    var(--asc-progress-circle-value-line-height);
    letter-spacing: var(--asc-progress-circle-value-letter-spacing, 0);
    color:          var(--asc-progress-circle-value-color, var(--asc-text-primary));
  }

  /* ─── Sublabel below ring (xxs only) ─── */
  /* Slot element; only rendered when size is xxs and sublabel content is assigned */
  [part='sublabel-below'] {
    display:     block;
    font-family: var(--asc-progress-circle-sublabel-font-family, var(--asc-font-family-body));
    font-size:   var(--asc-progress-circle-sublabel-font-size);
    font-weight: var(--asc-progress-circle-sublabel-font-weight, var(--asc-font-weight-medium));
    line-height: var(--asc-progress-circle-sublabel-line-height);
    color:       var(--asc-progress-circle-sublabel-color, var(--asc-text-tertiary));
    text-align:  center;
  }
`;
